import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  Gamepad2, Play, Heart, Copy, Search, Filter, TrendingUp, Clock, GitBranch,
  ArrowLeft, Loader2, Users, Eye, Sparkles, ChevronDown, Share2, Repeat
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import FeaturedGameSpotlight from '../components/FeaturedGameSpotlight';
import CommentsBlock from '../components/CommentsBlock';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function TreeNode({ node, depth, onPlay }) {
  return (
    <div style={{ marginLeft: depth * 18 }} className={depth > 0 ? 'border-l border-[#1E1E24] pl-3 mt-1.5' : ''}>
      <div className="flex items-center gap-2 py-1">
        {depth > 0 && <span className="text-zinc-600 text-xs">⑂</span>}
        <button
          onClick={() => node.published && onPlay(node.project_id)}
          disabled={!node.published}
          className={`text-sm truncate max-w-[220px] text-left ${node.published ? 'text-white hover:text-[#06B6D4]' : 'text-zinc-500 cursor-default'}`}
          data-testid={`tree-node-${node.project_id}`}
          title={node.published ? 'Play this game' : 'Private remix (not published)'}
        >
          {node.name}
        </button>
        <span className="text-[10px] text-zinc-500 truncate">by {node.author}</span>
        {node.creator_badge && <span className="text-[10px] text-amber-300">★</span>}
        {!node.published && <span className="text-[9px] text-zinc-600 border border-[#1E1E24] rounded-full px-1.5">private</span>}
      </div>
      {/* React.createElement avoids a babel-plugin recursion bug with self-referencing JSX */}
      {(node.children || []).map((c) => React.createElement(TreeNode, { key: c.project_id, node: c, depth: depth + 1, onPlay }))}
    </div>
  );
}

const genreColors = {
  platformer: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  fps: 'bg-red-500/20 text-red-400 border-red-500/30',
  rpg: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  puzzle: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  simulation: 'bg-blue-500/20 text-blue-400 border-blue-500/30'
};

export default function Gallery() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedGenre, setSelectedGenre] = useState('');
  const [sortBy, setSortBy] = useState('recent');
  const [showFilters, setShowFilters] = useState(false);
  const [remixTarget, setRemixTarget] = useState(null);
  const [twist, setTwist] = useState('');
  const [remixBusy, setRemixBusy] = useState(false);
  const [treeTarget, setTreeTarget] = useState(null);
  const [tree, setTree] = useState(null);

  useEffect(() => {
    fetchGallery();
  }, [selectedGenre, sortBy]);

  const fetchGallery = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (selectedGenre) params.append('genre', selectedGenre);
      params.append('sort', sortBy);
      params.append('limit', '50');
      
      const res = await axios.get(`${API}/gallery?${params.toString()}`);
      setGames(res.data.games || []);
    } catch (err) {
      console.error('Failed to fetch gallery:', err);
      toast.error('Failed to load community games');
    } finally {
      setLoading(false);
    }
  };

  const handleLike = async (projectId, e) => {
    e.stopPropagation();
    if (!isAuthenticated) {
      toast.error('Sign in to like games');
      return;
    }
    
    try {
      const res = await axios.post(`${API}/projects/${projectId}/like`, {}, { withCredentials: true });
      setGames(games.map(g => 
        g.project_id === projectId 
          ? { ...g, likes_count: (g.likes_count || 0) + (res.data.liked ? 1 : -1), liked: res.data.liked }
          : g
      ));
      toast.success(res.data.liked ? 'Liked!' : 'Unliked');
    } catch (err) {
      toast.error('Failed to like game');
    }
  };

  const handleRemix = (game, e) => {
    e.stopPropagation();
    if (!isAuthenticated) {
      toast.info('Sign up free to remix this game — takes 10 seconds', {
        action: {
          label: 'Sign up',
          onClick: () => navigate(`/register?next=/gallery`),
        },
        duration: 6000,
      });
      return;
    }
    setTwist('');
    setRemixTarget(game);
  };

  const openTree = async (game, e) => {
    e.stopPropagation();
    setTreeTarget(game);
    setTree(null);
    try {
      const res = await axios.get(`${API}/gallery/${game.project_id}/remix-tree`);
      setTree(res.data.tree);
    } catch {
      toast.error('Could not load remix tree');
      setTreeTarget(null);
    }
  };

  const doAiRemix = async () => {
    setRemixBusy(true);
    try {
      const res = await axios.post(`${API}/gamegen/remix`,
        { source_project_id: remixTarget.project_id, twist }, { withCredentials: true });
      setRemixTarget(null);
      navigate(`/generate?job=${res.data.job_id}&project=${res.data.project_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start AI remix');
    } finally {
      setRemixBusy(false);
    }
  };

  const doClone = async () => {
    setRemixBusy(true);
    try {
      const res = await axios.post(`${API}/projects/${remixTarget.project_id}/remix`, {}, { withCredentials: true });
      toast.success('Game cloned! Opening editor...');
      navigate(`/editor/${res.data.project_id}`);
    } catch (err) {
      toast.error('Failed to remix game');
    } finally {
      setRemixBusy(false);
    }
  };

  const handleShare = async (projectId, e) => {
    e.stopPropagation();
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/share/${projectId}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Share link copied!', { description: 'Rich preview card included.' });
    } catch {
      toast.error('Failed to copy link');
    }
  };

  const filteredGames = games.filter(game => 
    !searchQuery || 
    game.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    game.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const genres = ['platformer', 'fps', 'rpg', 'puzzle', 'simulation'];

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="gallery-page">
      {/* Header */}
      <header className="border-b border-[#1E1E24] bg-[#0A0A0C]/80 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-[1400px] mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate('/')}
              className="text-zinc-400 hover:text-white"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-sm bg-[#06B6D4]/20 border border-[#06B6D4]/50 flex items-center justify-center">
                <Users className="w-4 h-4 text-[#06B6D4]" />
              </div>
              <span className="font-bold text-lg text-white">Community Gallery</span>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {isAuthenticated ? (
              <Button 
                onClick={() => navigate('/dashboard')} 
                className="bg-[#06B6D4] text-black hover:bg-[#06B6D4]/90"
              >
                My Games
              </Button>
            ) : (
              <Button 
                onClick={() => navigate('/login')} 
                className="bg-white text-black hover:bg-zinc-200"
              >
                Sign In to Create
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Filters Bar */}
      <div className="border-b border-[#1E1E24] bg-[#0A0A0C]/50">
        <div className="max-w-[1400px] mx-auto px-6 py-4">
          <div className="flex items-center gap-4 flex-wrap">
            {/* Search */}
            <div className="relative flex-1 min-w-[200px] max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <Input
                placeholder="Search games..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-[#1E1E24] border-[#2E2E34] text-white"
                data-testid="gallery-search"
              />
            </div>

            {/* Sort */}
            <div className="flex items-center gap-2">
              <Button
                variant={sortBy === 'recent' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setSortBy('recent')}
                className={sortBy === 'recent' ? 'bg-[#06B6D4] text-black' : 'text-zinc-400'}
              >
                <Clock className="w-4 h-4 mr-1" />
                Recent
              </Button>
              <Button
                variant={sortBy === 'popular' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setSortBy('popular')}
                className={sortBy === 'popular' ? 'bg-[#06B6D4] text-black' : 'text-zinc-400'}
              >
                <TrendingUp className="w-4 h-4 mr-1" />
                Popular
              </Button>
              <Button
                variant={sortBy === 'likes' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setSortBy('likes')}
                className={sortBy === 'likes' ? 'bg-[#06B6D4] text-black' : 'text-zinc-400'}
              >
                <Heart className="w-4 h-4 mr-1" />
                Most Liked
              </Button>
            </div>

            {/* Genre Filter */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
              className="text-zinc-400 hover:text-white"
            >
              <Filter className="w-4 h-4 mr-1" />
              Genres
              <ChevronDown className={`w-4 h-4 ml-1 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
            </Button>
          </div>

          {/* Genre Pills */}
          {showFilters && (
            <div className="flex items-center gap-2 mt-4 flex-wrap">
              <Button
                variant={!selectedGenre ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setSelectedGenre('')}
                className={!selectedGenre ? 'bg-zinc-700' : 'text-zinc-400'}
              >
                All
              </Button>
              {genres.map(genre => (
                <Button
                  key={genre}
                  variant={selectedGenre === genre ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setSelectedGenre(genre)}
                  className={`capitalize ${selectedGenre === genre ? genreColors[genre] : 'text-zinc-400'}`}
                >
                  {genre}
                </Button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Featured Game of the Week */}
      <div className="max-w-[1400px] mx-auto px-6 pt-8">
        <FeaturedGameSpotlight variant="gallery" />
      </div>

      {/* Fresh Wave — newest games spotlight carousel */}
      {!searchQuery && !selectedGenre && games.length > 0 && (
        <div className="max-w-[1400px] mx-auto px-6 pt-6" data-testid="fresh-wave-row">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-xs font-mono uppercase tracking-widest text-[#06B6D4]">◢ Fresh Wave</span>
            <span className="text-[10px] text-zinc-600">newly forged — ranged combat, impact FX & new genres</span>
          </div>
          <div className="flex gap-4 overflow-x-auto pb-2 snap-x snap-mandatory">
            {[...games]
              .sort((a, b) => (b.published_at || b.created_at || '').localeCompare(a.published_at || a.created_at || ''))
              .slice(0, 5)
              .map(g => (
                <button
                  key={g.project_id}
                  onClick={() => navigate(`/play/${g.project_id}`)}
                  className="relative shrink-0 w-[380px] max-w-[85vw] h-[200px] rounded-xl overflow-hidden border border-[#1E1E24] hover:border-[#06B6D4]/60 snap-start group text-left transition-colors"
                  data-testid={`fresh-wave-card-${g.project_id}`}
                >
                  {g.thumbnail ? (
                    <img src={g.thumbnail} alt={g.name} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                  ) : (
                    <div className="absolute inset-0 bg-gradient-to-br from-[#0E1A2B] to-[#050505]" />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/25 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <p className="text-[9px] font-mono uppercase tracking-widest text-[#67E8F9] mb-1">{g.genre}</p>
                    <h3 className="text-white font-bold text-lg leading-tight truncate">{g.name}</h3>
                    <p className="text-[11px] text-zinc-400 line-clamp-1">{g.description}</p>
                  </div>
                  <span className="absolute top-3 right-3 text-[9px] font-mono bg-[#06B6D4] text-black font-bold px-2 py-0.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">▶ PLAY</span>
                  <span className="absolute top-3 left-3 flex items-center gap-1.5">
                    <span className="text-[9px] font-mono bg-black/60 backdrop-blur border border-[#06B6D4]/40 text-[#67E8F9] px-2 py-0.5 rounded-full">NEW</span>
                    <span className="text-[9px] font-mono bg-black/60 backdrop-blur border border-white/10 text-zinc-300 px-2 py-0.5 rounded-full" data-testid={`fresh-wave-plays-${g.project_id}`} title="Total plays">
                      ▶ {g.play_count || 0} {(g.play_count || 0) === 1 ? 'play' : 'plays'}
                    </span>
                  </span>
                </button>
              ))}
          </div>
        </div>
      )}

      {/* Guest conversion nudge — subtle, only for anonymous visitors */}
      {!isAuthenticated && (
        <div className="max-w-[1400px] mx-auto px-6 pt-6">
          <div className="flex items-center justify-between gap-4 px-4 py-2.5 rounded-full bg-[#06B6D4]/5 border border-[#06B6D4]/20 text-xs" data-testid="guest-gallery-banner">
            <p className="text-zinc-400">
              <span className="text-emerald-300">✨ Playing as guest.</span>{' '}
              <span className="text-zinc-500">Sign up to save your remixes and unlock the editor.</span>
            </p>
            <Button
              size="sm"
              onClick={() => navigate('/register')}
              className="bg-white text-black hover:bg-zinc-200 rounded-full h-7 text-xs px-4"
              data-testid="guest-signup-cta"
            >
              Sign up free →
            </Button>
          </div>
        </div>
      )}

      {/* Gallery Grid */}
      <main className="max-w-[1400px] mx-auto px-6 py-8">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 text-[#06B6D4] animate-spin" />
          </div>
        ) : filteredGames.length === 0 ? (
          <div className="text-center py-16">
            <Gamepad2 className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-zinc-400 mb-2">No games found</h3>
            <p className="text-zinc-500 mb-6">Be the first to publish a game!</p>
            {isAuthenticated ? (
              <Button onClick={() => navigate('/dashboard')} className="bg-[#06B6D4] text-black">
                Create a Game
              </Button>
            ) : (
              <Button onClick={() => navigate('/register')} className="bg-white text-black">
                Get Started
              </Button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredGames.map(game => (
              <div
                key={game.project_id}
                className="group bg-[#0A0A0C] border border-[#1E1E24] rounded-lg overflow-hidden hover:border-[#06B6D4]/50 transition-all cursor-pointer"
                onClick={() => navigate(`/play/${game.project_id}`)}
                data-testid={`game-card-${game.project_id}`}
              >
                {/* Thumbnail */}
                <div className="aspect-video bg-[#1E1E24] relative overflow-hidden">
                  <img
                    src={game.thumbnail || `${API}/og/${game.project_id}.png`}
                    alt={game.name}
                    className="w-full h-full object-cover"
                    onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.parentElement.querySelector('.thumb-fallback').style.display = 'flex'; }}
                  />
                  <div className="thumb-fallback w-full h-full absolute inset-0 hidden items-center justify-center bg-[#1E1E24]">
                    <Gamepad2 className="w-12 h-12 text-zinc-700" />
                  </div>
                  
                  {/* Play overlay */}
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <div className="w-16 h-16 rounded-full bg-[#06B6D4] flex items-center justify-center">
                      <Play className="w-8 h-8 text-black ml-1" />
                    </div>
                  </div>

                  {/* Genre badge */}
                  <div className={`absolute top-2 left-2 px-2 py-0.5 rounded text-xs font-medium border ${genreColors[game.genre] || genreColors.platformer}`}>
                    {game.genre?.toUpperCase()}
                  </div>
                </div>

                {/* Info */}
                <div className="p-4">
                  <h3 className="font-semibold text-white truncate mb-1">{game.name}</h3>
                  <p className="text-sm text-zinc-500 truncate mb-1 flex items-center gap-1.5">
                    by {game.author?.name || 'Unknown'}
                    {game.author?.creator_badge && (
                      <span className="inline-flex items-center gap-0.5 text-[10px] font-bold text-amber-300 bg-amber-500/15 border border-amber-500/30 rounded-full px-1.5 py-px" data-testid={`creator-badge-${game.project_id}`} title="Creator — featured more than once">
                        ★ Creator
                      </span>
                    )}
                  </p>
                  {(game.remix_count || 0) > 0 && (
                    <button
                      onClick={(e) => openTree(game, e)}
                      className="flex items-center gap-1 text-xs text-zinc-400 hover:text-[#06B6D4] mb-1"
                      data-testid={`remix-tree-btn-${game.project_id}`}
                      title="See the family tree of remixes this game inspired"
                    >
                      <GitBranch className="w-3 h-3" /> {game.remix_count} remix{game.remix_count > 1 ? 'es' : ''} — view tree
                    </button>
                  )}
                  {game.remixed_from?.project_id && (
                    <button
                      onClick={(e) => { e.stopPropagation(); navigate(`/play/${game.remixed_from.project_id}`); }}
                      className="flex items-center gap-1 text-xs text-[#06B6D4]/80 hover:text-[#06B6D4] truncate mb-2 max-w-full"
                      data-testid={`remix-lineage-${game.project_id}`}
                      title={`Play the original by ${game.remixed_from.author_name || 'Unknown'}`}
                    >
                      <Repeat className="w-3 h-3 shrink-0" />
                      <span className="truncate">remixed from "{game.remixed_from.name}" by {game.remixed_from.author_name || 'Unknown'}</span>
                    </button>
                  )}
                  
                  {/* Stats */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 text-xs text-zinc-500">
                      <span className="flex items-center gap-1">
                        <Eye className="w-3 h-3" />
                        {game.play_count || 0}
                      </span>
                      <span className="flex items-center gap-1">
                        <Heart className="w-3 h-3" />
                        {game.likes_count || 0}
                      </span>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => handleLike(game.project_id, e)}
                        className={`h-8 w-8 p-0 ${game.liked ? 'text-red-500' : 'text-zinc-500 hover:text-red-500'}`}
                        data-testid={`like-btn-${game.project_id}`}
                      >
                        <Heart className={`w-4 h-4 ${game.liked ? 'fill-current' : ''}`} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => handleRemix(game, e)}
                        className="h-8 w-8 p-0 text-zinc-500 hover:text-[#06B6D4]"
                        data-testid={`remix-btn-${game.project_id}`}
                        title="Remix — AI-regenerate with your twist, or clone it"
                      >
                        <Repeat className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => handleShare(game.project_id, e)}
                        className="h-8 w-8 p-0 text-zinc-500 hover:text-[#06B6D4]"
                        data-testid={`share-btn-${game.project_id}`}
                        title="Copy share link with rich preview"
                      >
                        <Share2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  {/* Community comments */}
                  <div onClick={(e) => e.stopPropagation()} role="presentation">
                    <CommentsBlock projectId={game.project_id} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Remix Tree Dialog */}
      <Dialog open={!!treeTarget} onOpenChange={(o) => !o && setTreeTarget(null)}>
        <DialogContent className="bg-[#0A0A0C] border-[#1E1E24] text-white max-w-md" data-testid="remix-tree-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GitBranch className="w-5 h-5 text-[#06B6D4]" /> Remix Family Tree
            </DialogTitle>
          </DialogHeader>
          {!tree ? (
            <div className="py-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-[#06B6D4]" /></div>
          ) : (
            <div className="max-h-[50vh] overflow-y-auto">
              <TreeNode node={tree} depth={0} onPlay={(pid) => { setTreeTarget(null); navigate(`/play/${pid}`); }} />
              {tree.children.length === 0 && (
                <p className="text-zinc-500 text-sm mt-2">No remixes yet — be the first to remix it!</p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Remix Dialog */}
      <Dialog open={!!remixTarget} onOpenChange={(o) => !o && setRemixTarget(null)}>
        <DialogContent className="bg-[#0A0A0C] border-[#1E1E24] text-white max-w-md" data-testid="remix-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Repeat className="w-5 h-5 text-[#06B6D4]" /> Remix "{remixTarget?.name}"
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm text-zinc-400 block mb-2">Your twist (optional)</label>
              <Textarea
                value={twist}
                onChange={(e) => setTwist(e.target.value)}
                rows={3}
                placeholder="e.g. set it underwater, make the hero a cat, turn it into a boss rush…"
                className="bg-[#0F0F12] border-[#1E1E24] text-white"
                data-testid="remix-twist-input"
              />
            </div>
            <Button
              onClick={doAiRemix}
              disabled={remixBusy}
              className="w-full bg-[#06B6D4] text-black font-bold"
              data-testid="remix-ai-btn"
            >
              {remixBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
              AI Remix — generate a new game
            </Button>
            <Button
              onClick={doClone}
              disabled={remixBusy}
              variant="outline"
              className="w-full border-[#1E1E24] text-zinc-300"
              data-testid="remix-clone-btn"
            >
              <Copy className="w-4 h-4 mr-2" /> Exact clone to my dashboard
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
